import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Center,
  Container,
  Group,
  Loader,
  Progress,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import { LogoPaper, PageTitle, StatusBadge } from '@/components';
import { useScreeningBatches, useTeams } from '@/hooks';
import { ScreeningBatchStatus, ScreeningBatchSummary } from '@/types';

const BATCH_STATUS_COLORS: Record<ScreeningBatchStatus, string> = {
  pending: 'gray',
  running: 'blue',
  paused: 'yellow',
  completed: 'green',
};

const BATCH_STATUS_LABELS: Record<ScreeningBatchStatus, string> = {
  pending: 'Not started',
  running: 'Running',
  paused: 'Paused',
  completed: 'Completed',
};

function BatchProgress({ batch }: Readonly<{ batch: ScreeningBatchSummary }>) {
  const { total, completed, failed } = batch.counts;
  if (total === 0) {
    return <Text c="dimmed">—</Text>;
  }
  return (
    <Stack gap={4}>
      <Progress.Root size="sm">
        <Progress.Section value={(completed / total) * 100} color="green" />
        <Progress.Section value={(failed / total) * 100} color="red" />
      </Progress.Root>
      <Text size="xs" c="dimmed">
        {completed + failed}/{total} done
        {failed > 0 ? ` · ${failed} failed` : ''}
      </Text>
    </Stack>
  );
}

export function ScreeningBatchesPage() {
  const navigate = useNavigate();
  const { batches, isLoading, error } = useScreeningBatches();
  const { teams } = useTeams();

  const teamNames = useMemo(
    () => new Map(teams.map((team) => [String(team.id), team.name])),
    [teams]
  );

  return (
    <Container size="xl" py="md">
      <Stack>
        <Group justify="space-between">
          <PageTitle title="Screening Batches" />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate('/admin/screening-batches/new')}
          >
            New batch
          </Button>
        </Group>

        <LogoPaper>
          {isLoading && batches.length === 0 ? (
            <Center h={200}>
              <Loader />
            </Center>
          ) : null}
          {error ? (
            <Text c="red" p="md">
              Could not load screening batches: {error.message}
            </Text>
          ) : null}
          {!isLoading && !error && batches.length === 0 ? (
            <Text c="dimmed" p="md">
              No screening batches yet. Create one to bulk-run basic screening reports into a client
              team.
            </Text>
          ) : null}
          {batches.length > 0 ? (
            <Table.ScrollContainer minWidth={720}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Batch</Table.Th>
                    <Table.Th>Team</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Subjects</Table.Th>
                    <Table.Th>Progress</Table.Th>
                    <Table.Th>Ratings</Table.Th>
                    <Table.Th>Created</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {batches.map((batch) => (
                    <Table.Tr
                      key={batch.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/screening-batches/${batch.id}`)}
                    >
                      <Table.Td>
                        <Text fw={500}>{batch.label}</Text>
                      </Table.Td>
                      <Table.Td>{teamNames.get(batch.team_id) ?? batch.team_id}</Table.Td>
                      <Table.Td>
                        <StatusBadge
                          label={BATCH_STATUS_LABELS[batch.status]}
                          color={BATCH_STATUS_COLORS[batch.status]}
                        />
                      </Table.Td>
                      <Table.Td>{batch.counts.total}</Table.Td>
                      <Table.Td>
                        <BatchProgress batch={batch} />
                      </Table.Td>
                      <Table.Td>
                        {batch.counts.ratings.hit + batch.counts.ratings.potential > 0 ? (
                          <Text size="sm">
                            {batch.counts.ratings.hit} hit · {batch.counts.ratings.potential}{' '}
                            potential
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            {batch.counts.ratings.clear > 0 ? 'All clear' : '—'}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : '—'}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : null}
        </LogoPaper>
      </Stack>
    </Container>
  );
}
